import { Injectable } from '@angular/core';

import { SnSharesAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth/services/current-organization.service';
import { SnDrawerConfig, SnDrawerRef, SnDrawerService } from '@lib/ui/drawer';

import { FUSUS_CROSS_REGION_DEV } from '../constants';
import { IShareConfigDrawerDataV2, ShareConfigDrawerV2Container } from '../containers/share-config-drawer-v2/share-config-drawer-v2.container';
import {
  IShareConfigDrawerData,
  ShareConfigDrawerContainer,
} from '../containers/share-config-drawer/share-config-drawer.container';
import { IFsShareDrawerV2Data, ShareDrawerV2Container } from '../containers/share-drawer-v2/share-drawer-v2.container';
import {
  ISnShareDrawerData,
  ShareDrawerContainer,
} from '../containers/share-drawer/share-drawer.container';
import {
  IShareInviteDrawerV2Data,
  ShareInviteDrawerV2Container,
} from '../containers/share-invite-drawer-v2/share-invite-drawer-v2.container';
import {
  IShareInviteDrawerData,
  ShareInviteDrawerContainer,
} from '../containers/share-invite-drawer/share-invite-drawer.container';
import { ISnCanShare } from '../interfaces';

@Injectable()
export class SnShareUiService {
  constructor(
    private readonly _drawer: SnDrawerService,
    private readonly _sharesAPI: SnSharesAPI,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {}

  public shareSettings(
    data: ISnShareDrawerData,
    config?: SnDrawerConfig<ISnShareDrawerData>,
  ): SnDrawerRef<ShareDrawerContainer | ShareDrawerV2Container, ISnShareDrawerData | IFsShareDrawerV2Data> {
    const isFususCrossRegionDevEnabled = this._currentOrganization.settings[FUSUS_CROSS_REGION_DEV];
    if (isFususCrossRegionDevEnabled) {
      return this._drawer.open<ShareDrawerV2Container, IFsShareDrawerV2Data>(ShareDrawerV2Container, {
        disableClose: true,
        ...config,
        data: data as IFsShareDrawerV2Data,
      });
    }
    return this._drawer.open<ShareDrawerContainer, ISnShareDrawerData>(ShareDrawerContainer, {
      disableClose: true,
      ...config,
      data,
    });
  }

  public createShare(
    data: IShareInviteDrawerData,
    config?: SnDrawerConfig<IShareInviteDrawerData>,
  ): SnDrawerRef<ShareInviteDrawerContainer | ShareInviteDrawerV2Container, IShareInviteDrawerData | IShareInviteDrawerV2Data> {
    const isFususCrossRegionDevEnabled = this._currentOrganization.settings[FUSUS_CROSS_REGION_DEV];
    
    if (isFususCrossRegionDevEnabled) {
      // Use V2 component when fususCrossRegionDev flag is true
      return this._drawer.open(ShareInviteDrawerV2Container, {
        disableClose: true,
        ...config,
        data: data as IShareInviteDrawerV2Data,
      });
    }
    
    // Default to V1 component when flag is false or undefined
    return this._drawer.open(ShareInviteDrawerContainer, {
      disableClose: true,
      ...config,
      data,
    });
  }

  public shareConfig(
    data: IShareConfigDrawerData,
    config?: SnDrawerConfig,
  ): SnDrawerRef<ShareConfigDrawerContainer | ShareConfigDrawerV2Container, IShareConfigDrawerData | IShareConfigDrawerDataV2> {
    const isFususCrossRegionDevEnabled = this._currentOrganization.settings[FUSUS_CROSS_REGION_DEV];
    if (isFususCrossRegionDevEnabled) {
      return this._drawer.open(ShareConfigDrawerV2Container, {
        disableClose: true,
        ...config,
        data: {
          updateHandler: this._defaultUpdateHandler.bind(this),
          ...data,
        },
      });
    }
    
    return this._drawer.open(ShareConfigDrawerContainer, {
      disableClose: true,
      ...config,
      data: {
        updateHandler: this._defaultUpdateHandler.bind(this),
        ...data,
      },
    });
  }

  private _defaultUpdateHandler(
    objectId: string,
    shareId: string,
    formData: Record<string, unknown>,
  ): ReturnType<ISnCanShare['updateShare']> {
    return this._sharesAPI.editShare(shareId, formData);
  }
}
